/**
 * Posting schedule + Evergreen library — Learn-Mode inline tips (lane B3).
 *
 * Anchored across `routes/social/PostingSchedule.tsx`,
 * `routes/social/EvergreenLibrary.tsx` and `components/queue/WeeklySlotGrid.tsx`.
 */

import type { LearnTip } from "./types";

export const queueTips: LearnTip[] = [
  {
    key: "social-queue",
    title: "Posting schedule",
    body: "Set the weekly times each account posts. Add to queue drops a post into the next free time; empty times can reuse evergreen posts.",
  },
  {
    key: "social-queue-controls",
    title: "Automatic filling",
    body: "Pause stops evergreen posts from filling empty times for this client. Posts already scheduled keep their times.",
    bullets: [
      "Auto-approve off (recommended): recycled posts wait in Approvals before they publish.",
      "Unapproved recycled posts are skipped a day after their time passes.",
    ],
  },
  {
    key: "social-queue-grid",
    title: "Weekly slots",
    body: "Each column is a day of the week. Click a time to give it a content category, pause it, or remove it. Times use the client's timezone, so daylight-saving changes are handled for you.",
  },
  {
    key: "social-queue-suggest",
    title: "Suggest from best times",
    body: "Fills the grid with the hours this account's posts got the most engagement. With too little history it uses typical best times and says so. Nothing is saved until you click Save schedule.",
  },
  {
    key: "social-queue-categories",
    title: "Content categories",
    body: "Group evergreen posts (for example Tips or Promotions). A slot with a category only recycles posts from that category.",
  },
  {
    key: "social-queue-upcoming",
    title: "Next 14 days",
    body: "Every slot time coming up: scheduled posts, what will be filled from the library, open times, and times another post already uses.",
  },
  {
    key: "social-evergreen",
    title: "Evergreen library",
    body: "Posts worth sharing again. When a slot with the same category is empty, the queue reuses one after its minimum gap, rotating its caption variations.",
  },
  {
    key: "social-evergreen-categories",
    title: "Categories",
    body: "Filter the library by content category. Set categories up on the Posting schedule page.",
  },
  {
    key: "social-evergreen-items",
    title: "Evergreen posts",
    body: "Each card shows how often the post has gone out and when it can go again. Stale posts are flagged so you can check prices, offers and links are still right. Generate variations asks the AI writer for fresh captions you can keep or discard.",
  },
  {
    key: "social-evergreen-variations",
    title: "Variations",
    body: "Different captions for the same post. The one used longest ago goes out next. Pinterest and Google Business need at least three before they recycle.",
  },
  {
    key: "social-evergreen-picker",
    title: "Add an existing post",
    body: "Pick a published post to reuse. Its caption, hashtags and images become the first variation.",
  },
  {
    key: "social-evergreen-generate",
    title: "AI variations",
    body: "Rewrites the original caption in the client's voice with a new opening and length. Facts, prices, links, disclosures and hashtags stay the same, and each one fits the networks this client posts to.",
    bullets: ["Nothing is saved until you click Save selected.", "Written by the AI model on our own server."],
  },
  {
    key: "social-evergreen-generate-previews",
    title: "Pick the ones to keep",
    body: "Tick the rewrites you like. Each shows its length and whether it fits every network. Kept ones join the post's variations and rotate when it recycles.",
  },
];
